import type { PctCount, UsageSnapshot } from '../../services/analytics';
import { DASH, formatDecimal, formatInt, formatPct } from './format';
import type { MetricInfoKey } from './metricInfo';

export type UsageCardId = Extract<
    MetricInfoKey,
    | 'crfa'
    | 'dau'
    | 'wau'
    | 'mau'
    | 'ausentes'
    | 'nuncaAcessou'
    | 'usuariosUnicosDia'
    | 'loginsDia'
    | 'loginsPorConta30d'
    | 'sessoesPorContaMes'
>;

export interface UsageCardView {
    id: UsageCardId;
    title: string;
    value: string;
    caption: string;
    highlight?: boolean;
}

/** "19,2% de 26 contas com acesso". Sem base, nao ha percentual. */
export function accessCaption(c: PctCount): string {
    if (c.base <= 0 || c.pct === null) return 'Sem contas com acesso';
    return `${formatPct(c.pct)} de ${formatInt(c.base)} ${c.base === 1 ? 'conta com acesso' : 'contas com acesso'}`;
}

/** "10/2026" a partir do dia YYYY-MM-DD. */
export function monthYear(day: string): string {
    const [y, m] = day.split('-');
    return y && m ? `${m}/${y}` : DASH;
}

const countOrDash = (c: PctCount): string => (c.base > 0 ? formatInt(c.count) : DASH);

/** Cards de North Star e acesso, na ordem e com os textos do prototipo. */
export function buildUsageCards(u: UsageSnapshot): UsageCardView[] {
    const g = u.geral;
    return [
        {
            id: 'crfa',
            title: 'Conta com rotina financeira ativa · CRFA',
            value: countOrDash(u.crfa),
            caption:
                u.crfa.base > 0 && u.crfa.pct !== null
                    ? `${formatPct(u.crfa.pct)} das pagantes ativas`
                    : 'Sem pagantes ativas',
            highlight: true,
        },
        { id: 'dau', title: 'DAU', value: countOrDash(g.dau), caption: accessCaption(g.dau) },
        { id: 'wau', title: 'WAU', value: countOrDash(g.wau), caption: accessCaption(g.wau) },
        { id: 'mau', title: 'MAU', value: countOrDash(g.mau), caption: accessCaption(g.mau) },
        { id: 'ausentes', title: 'Ausentes', value: countOrDash(g.ausentes), caption: accessCaption(g.ausentes) },
        {
            id: 'nuncaAcessou',
            title: 'Nunca acessou',
            value: countOrDash(g.nuncaAcessou),
            caption: accessCaption(g.nuncaAcessou),
        },
        {
            id: 'usuariosUnicosDia',
            title: 'Usuários únicos com acesso · dia',
            value: formatInt(u.usuariosUnicosDia),
            caption: 'IDs distintos, não contas',
        },
        {
            id: 'loginsDia',
            title: 'Logins · dia',
            value: formatInt(u.loginsDia),
            caption: 'Logins com senha concluídos',
        },
        {
            id: 'loginsPorConta30d',
            title: 'Logins/conta · 30 dias',
            value: formatDecimal(u.loginsPorConta30d),
            caption: 'Média por conta com acesso',
        },
        {
            id: 'sessoesPorContaMes',
            title: 'Sessões/conta · mês',
            value: formatDecimal(u.sessoesPorContaMes),
            caption: `Sessões iniciadas em ${monthYear(u.day)}`,
        },
    ];
}
